/* eslint-disable complexity */
import {Locale} from '@aboutyou/config';
import {useCurrencyConfig} from './I18nContext';

type NumericalDelimiter = ',' | '.';

export enum CurrencyCode {
  BGN = 'BGN',
  CHF = 'CHF',
  CZK = 'CZK',
  DKK = 'DKK',
  EUR = 'EUR',
  HRK = 'HRK',
  HUF = 'HUF',
  ILS = 'ILS',
  NOK = 'NOK',
  PLN = 'PLN',
  RON = 'RON',
  SAR = 'SAR',
  SEK = 'SEK',
  USD = 'USD',
}

export interface Currency {
  decimalPoint: NumericalDelimiter;
  iso: string;
  negativeSign: string;
  sign: Record<string, string>;
  signPosition: 'l' | 'r';
  truncate: boolean;
  thousandsSeparator: NumericalDelimiter | ' ' | "'" | '';
  useIsoSign: boolean;
  hideVatInfo?: boolean;
}

export const getCurrencyConfigByLocale_UNSAFE = (locale: Locale): Currency => {
  switch (locale) {
    case Locale.en_ZZ:
    case Locale.es_ZZ:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.USD,
        sign: {
          [CurrencyCode.USD]: '$',
          [CurrencyCode.SAR]: '$',
          [CurrencyCode.ILS]: '$',
        },
        negativeSign: '-',
        signPosition: 'l',
        thousandsSeparator: ',',
        truncate: false,
        useIsoSign: false,
        hideVatInfo: true,
      };
    case Locale.ar_SA:
    case Locale.en_SA:
    case Locale.en_IL:
    case Locale.he_IL:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.USD,
        sign: {
          [CurrencyCode.USD]: '$',
          [CurrencyCode.SAR]: '$',
          [CurrencyCode.ILS]: '$',
        },
        negativeSign: '-',
        signPosition: 'l',
        thousandsSeparator: ',',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.en_DE:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.de_DE:
    case Locale.sk_SK:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.de_AT:
    case Locale.nl_NL:
    case Locale.nl_BE:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'l',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.de_CH:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.CHF,
        sign: {},
        negativeSign: '-',
        signPosition: 'l',
        thousandsSeparator: "'",
        truncate: false,
        useIsoSign: true,
      };
    case Locale.fr_CH:
    case Locale.it_CH:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.CHF,
        sign: {},
        negativeSign: '-',
        signPosition: 'l',
        thousandsSeparator: "'",
        truncate: false,
        useIsoSign: false,
      };
    case Locale.fr_BE:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.fr_FR:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.pl_PL:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.PLN,
        sign: {[CurrencyCode.PLN]: 'zł'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.cs_CZ:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.CZK,
        sign: {[CurrencyCode.CZK]: 'Kč'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: true,
        useIsoSign: false,
      };
    case Locale.hu_HU:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.HUF,
        sign: {[CurrencyCode.HUF]: 'Ft'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: true,
        useIsoSign: false,
      };
    case Locale.ro_RO:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.RON,
        sign: {[CurrencyCode.RON]: 'lei'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.sl_SI:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.lv_LV:
    case Locale.ru_LV:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.lt_LT:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.et_EE:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.ru_EE:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.hr_HR:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€', [CurrencyCode.HRK]: 'kn'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.bg_BG:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.BGN,
        sign: {[CurrencyCode.BGN]: 'лв.'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.da_DK:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.DKK,
        sign: {[CurrencyCode.DKK]: 'kr'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.en_IE:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'l',
        thousandsSeparator: ',',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.sv_SE:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.SEK,
        sign: {[CurrencyCode.SEK]: 'kr'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.fi_FI:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.es_ES:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.it_IT:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.pt_PT:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '.',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.el_GR:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: '',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.no_NO:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.NOK,
        sign: {[CurrencyCode.NOK]: 'kr'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.el_CY:
      return {
        decimalPoint: '.',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'l',
        thousandsSeparator: ',',
        truncate: false,
        useIsoSign: false,
      };
    case Locale.fr_LU:
      return {
        decimalPoint: ',',
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        signPosition: 'r',
        thousandsSeparator: ' ',
        truncate: false,
        useIsoSign: false,
      };
  }
};

const addSign = (price: string, signPosition: 'l' | 'r', sign: string) => {
  return signPosition === 'r' ? `${price} ${sign}` : `${sign} ${price}`;
};

const addThousandSeparator = (
  preDecimal: number,
  separator: NumericalDelimiter | ' ' | "'" | '',
): string => {
  const chars = String(preDecimal).split('');
  chars.splice(chars.length - 3, 0, separator);
  return chars.join('');
};

const formatPrice = (
  price: number,
  currencyCode: string,
  currency: Currency,
  options?: {
    withoutDecimal?: boolean;
    useIsoSign?: boolean;
  },
): string => {
  const {
    decimalPoint,
    negativeSign,
    sign,
    signPosition,
    thousandsSeparator,
    truncate,
    useIsoSign,
  } = currency;

  if (isNaN(price)) {
    return '';
  }

  const negativeStr = price < 0 ? negativeSign : '';
  const round = price < 0 ? Math.ceil : Math.floor;
  const preDecimal =
    options && options.withoutDecimal
      ? Math.abs(Math.round(price / 100))
      : Math.abs(round(price / 100));
  const preDecimalStr =
    preDecimal > 999
      ? addThousandSeparator(preDecimal, thousandsSeparator)
      : preDecimal;

  let afterTruncateCents = '';
  if (!(options && options.withoutDecimal)) {
    const rest = Math.abs(price % 100);
    const cents = rest < 10 ? `0${rest}` : rest;
    afterTruncateCents = truncate ? '' : `${decimalPoint}${cents}`;
  }

  const priceStr = `${negativeStr}${preDecimalStr}${afterTruncateCents}`;

  const isoSign = options?.useIsoSign ?? useIsoSign;

  return addSign(
    priceStr,
    signPosition,
    isoSign ? currencyCode : sign[currencyCode] ?? currencyCode,
  );
};

type FormatCurrencyOptions = Parameters<typeof formatPrice>[3];

const formatCurrency =
  (currencyConfig: Currency) =>
  (
    price: number,
    currencyCode: string,
    options?: FormatCurrencyOptions,
  ): string => {
    return formatPrice(price, currencyCode, currencyConfig, options);
  };

export const useFormatCurrency = () => {
  const currencyConfig = useCurrencyConfig();

  return formatCurrency(currencyConfig);
};
